import { NgModule } from '@angular/core';
import { FlexLayoutModule, FlexModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { FbCoreModule } from '@fleet-beat/core';
import { InfiniteListModule } from '@fleet-beat/infinite-list';
import { WrapPanelModule } from '@fleet-beat/wrap-panel';
import { NgxMaskModule } from 'ngx-mask';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { AnexComponent } from './components/anex/anex.component';
import { ArrayEditorComponent } from './components/array-editor/array-editor.component';
import { CheckboxListFieldComponent } from './components/checkbox-list-field/checkbox-list-field.component';
import { ColorFieldComponent } from './components/color-field/color-field.component';
import { DataAutocompleteComponent } from './components/data-autocomplete/data-autocomplete.component';
import { DataRadioComponent } from './components/data-radio/data-radio.component';
import { DataSelectComponent } from './components/data-select/data-select.component';
import { DateFieldComponent } from './components/date-field/date-field.component';
import { DateRangeFieldComponent } from './components/date-range-field/date-range-field.component';
import { DocumentUploadFieldComponent } from './components/document-upload-field/document-upload-field.component';
import { EditorFieldComponent } from './components/editor-field/editor-field.component';
import { EmailListFieldComponent } from './components/email-list-field/email-list-field.component';
import { FieldTooltipPasswordRulesComponent } from './components/field-tooltip-password-rules/field-tooltip-password-rules.component';
import { FieldTooltipComponent } from './components/field-tooltip/field-tooltip.component';
import { FormatFileIconComponent } from './components/format-file-icon/format-file-icon.component';
import { ImageSliderFieldComponent } from './components/image-slider-field/image-slider-field.component';
import { IosToggleComponent } from './components/ios-toggle/ios-toggle.component';
import { NumberFieldComponent } from './components/number-field/number-field.component';
import { NumberRangeFieldComponent } from './components/number-range-field/number-range-field.component';
import { PasswordFieldComponent } from './components/password-field/password-field.component';
import { PhoneFieldComponent } from './components/phone-field/phone-field.component';
import { RatingFieldComponent } from './components/rating-field/rating-field.component';
import { RatingStarsComponent } from './components/rating-stars/rating-stars.component';
import { SelectAutocompleteComponent } from './components/select-autocomplete/select-autocomplete.component';
import { TimepickerFieldComponent } from './components/timepicker-field/timepicker-field.component';
import { UploadFieldComponent } from './components/upload-field/upload-field.component';
import { UploadFileListFieldComponent } from './components/upload-file-list-field/upload-file-list-field.component';
import { ValidationErrorComponent } from './components/validation-error/validation-error.component';
import { FileUploadDragDropDirective } from './directives/file-upload-drag-drop.directive';
import { EditorComponent } from './editor/editor.component';
import { ArrayItemIndexPipe } from './pipes/array-item-index.pipe';
import { CombineTemplatesPipe } from './pipes/combine-templates.pipe';
import { CustomValidationErrorPipe } from './pipes/custom-validation-error.pipe';
import { DisabledAutocompleteItemPipe } from './pipes/disabled-autocomplete-item.pipe';
import { EditorColumnsPipe } from './pipes/editor-columns.pipe';
import { EditorCssClassPipe } from './pipes/editor-css-class.pipe';
import { EditorItemVisibilityPipe } from './pipes/editor-item-visibility.pipe';
import { FileToBase64Pipe } from './pipes/file-to-base64.pipe';
import { IncludesPipe } from './pipes/includes.pipe';
import { IsArrayPipe } from './pipes/isArray.pipe';
import { MaskToDefinitionsPipe } from './pipes/mask-to-definitions.pipe';
import { MaskToPatternPipe } from './pipes/mask-to-pattern.pipe';
import { NumberFieldMaskPipe } from './pipes/number-field-mask.pipe';
import { SelectedItemLabelPipe } from './pipes/selected-item-label.pipe';
import { TransformFieldConfigPipe } from './pipes/transorm-field-config.pipe';
import { TimepickerFieldV2Component } from './components/timepicker2-field/timepicker2-field.component';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';

@NgModule({
  imports: [
    FbCoreModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatDatepickerModule,
    MatIconModule,
    MatTooltipModule,
    MatNativeDateModule,
    MatMomentDateModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatSlideToggleModule,
    MatProgressBarModule,
    MatSelectModule,
    MatRadioModule,
    MatRippleModule,
    ReactiveFormsModule,
    FlexModule,
    FlexLayoutModule,
    InfiniteListModule,
    NgxMaskModule.forRoot(),
    NgxMaterialTimepickerModule,
    FormsModule,
    MatTreeModule,
    WrapPanelModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
  ],
  declarations: [
    EditorFieldComponent,
    EditorComponent,
    PhoneFieldComponent,
    DateRangeFieldComponent,
    EditorColumnsPipe,
    EditorCssClassPipe,
    UploadFieldComponent,
    EmailListFieldComponent,
    FieldTooltipComponent,
    FieldTooltipPasswordRulesComponent,
    TransformFieldConfigPipe,
    CombineTemplatesPipe,
    NumberFieldComponent,
    NumberFieldMaskPipe,
    TimepickerFieldComponent,
    MaskToPatternPipe,
    ColorFieldComponent,
    MaskToPatternPipe,
    CustomValidationErrorPipe,
    CheckboxListFieldComponent,
    DataAutocompleteComponent,
    DataRadioComponent,
    SelectedItemLabelPipe,
    SelectAutocompleteComponent,
    RatingFieldComponent,
    RatingStarsComponent,
    ValidationErrorComponent,
    ArrayEditorComponent,
    ArrayItemIndexPipe,
    IsArrayPipe,
    IncludesPipe,
    DisabledAutocompleteItemPipe,
    EditorItemVisibilityPipe,
    FileToBase64Pipe,
    AnexComponent,
    PasswordFieldComponent,
    FileUploadDragDropDirective,
    DataSelectComponent,
    IosToggleComponent,
    DocumentUploadFieldComponent,
    MaskToDefinitionsPipe,
    NumberRangeFieldComponent,
    ImageSliderFieldComponent,
    UploadFileListFieldComponent,
    FormatFileIconComponent,
    DateFieldComponent,
    TimepickerFieldV2Component,
  ],
  exports: [
    EditorFieldComponent,
    AnexComponent,
    EditorComponent,
    TransformFieldConfigPipe,
    NumberFieldMaskPipe,
    CombineTemplatesPipe,
    IosToggleComponent,
    CheckboxListFieldComponent,
    FormatFileIconComponent,
  ],
})
export class EditorModule {}
